import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { supabase } from "@/services/supabase";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [alreadyUsed, setAlreadyUsed] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get("session_id");
      
      if (!sessionId) {
        setVerificationError("No session ID provided");
        setVerifying(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { session_id: sessionId }
        });

        if (error) {
          setVerificationError(error.message || "Failed to verify payment");
          setVerifying(false);
          return;
        }

        if (!data.verified) {
          setVerificationError(data.error || "Payment verification failed");
          setAlreadyUsed(data.alreadyUsed || false);
          setVerifying(false);
          return;
        }

        // Payment verified successfully
        setVerified(true);
        setVerifiedEmail(data.email);
        setVerifying(false);
      } catch (err) {
        setVerificationError("An unexpected error occurred");
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  useEffect(() => {
    // Only start countdown if payment is verified
    if (!verified) return;

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    // Redirect to Studio app after 5 seconds
    const redirect = setTimeout(() => {
      const sessionId = searchParams.get("session_id");
      
      // Redirect to Studio app with verified session info
      window.location.href = `https://klauyros.artoverse.club/auth/signup?session_id=${sessionId || ''}&email=${verifiedEmail || ''}&verified=true`;
    }, 5000);

    return () => {
      clearInterval(timer);
      clearTimeout(redirect);
    };
  }, [verified, searchParams, verifiedEmail]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-20"
      style={{
        background: "linear-gradient(180deg, #000000 0%, #0a0a0f 50%, #000000 100%)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl text-center"
      >
        {/* Icon based on state */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          {verifying && (
            <Loader2 
              size={80} 
              className="mx-auto animate-spin"
              style={{ color: "#E67E22" }}
            />
          )}
          {verified && (
            <CheckCircle 
              size={80} 
              className="mx-auto"
              style={{ color: "#22C55E" }}
            />
          )}
          {verificationError && (
            <AlertCircle 
              size={80} 
              className="mx-auto"
              style={{ color: "#EF4444" }}
            />
          )}
        </motion.div>

        {/* Card */}
        <div 
          className="rounded-3xl p-8 md:p-12"
          style={{
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            boxShadow: "0 0 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Verifying State */}
          {verifying && (
            <>
              <h1 
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{
                  fontFamily: "serif",
                  color: "#FFFFFF",
                  textShadow: "0 0 30px rgba(230, 126, 34, 0.3)",
                }}
              >
                Verifying Payment...
              </h1>
              
              <p 
                className="text-lg md:text-xl"
                style={{ 
                  color: "rgba(255, 255, 255, 0.7)",
                  fontFamily: "'Bensoon', sans-serif",
                }}
              >
                Please wait while we verify your payment with Stripe
              </p>
            </>
          )}

          {/* Success State */}
          {verified && (
            <>
              <h1 
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{
                  fontFamily: "serif",
                  color: "#FFFFFF",
                  textShadow: "0 0 30px rgba(230, 126, 34, 0.3)",
                }}
              >
                Payment Successful!
              </h1>
              
              <p 
                className="text-lg md:text-xl mb-4"
                style={{ 
                  color: "rgba(255, 255, 255, 0.7)",
                  fontFamily: "'Bensoon', sans-serif",
                }}
              >
                Thank you for your purchase! 
              </p>

              <div 
                className="mb-8 p-4 rounded-lg"
                style={{
                  background: "rgba(230, 126, 34, 0.1)",
                  border: "1px solid rgba(230, 126, 34, 0.3)",
                }}
              >
                <p 
                  className="text-lg font-medium mb-2"
                  style={{ 
                    color: "#E67E22",
                    fontFamily: "'Bensoon', sans-serif",
                  }}
                >
                  <Loader2 className="inline-block animate-spin mr-2" size={20} />
                  Redirecting to Studio in {countdown} seconds...
                </p>
                <p 
                  className="text-sm"
                  style={{ 
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "'Bensoon', sans-serif",
                  }}
                >
                  You'll create your account and access your content
                </p>
              </div>

              {/* Action Button */}
              <a 
                href={`https://klauyros.artoverse.club/auth/signup?session_id=${searchParams.get("session_id") || ''}&email=${verifiedEmail || ''}&verified=true`}
                className="inline-block"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                  style={{
                    background: "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)",
                    color: "#000000",
                    fontFamily: "'Bensoon', sans-serif",
                    boxShadow: "0 4px 20px rgba(230, 126, 34, 0.4)",
                  }}
                >
                  Continue to Studio
                  <ArrowRight size={20} />
                </motion.button>
              </a>
            </>
          )}

          {/* Error State */}
          {verificationError && (
            <>
              <h1 
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{
                  fontFamily: "serif",
                  color: "#FFFFFF",
                  textShadow: "0 0 30px rgba(239, 68, 68, 0.3)",
                }}
              >
                {alreadyUsed ? "Payment Already Used" : "Verification Failed"}
              </h1>
              
              <p 
                className="text-lg md:text-xl mb-6"
                style={{ 
                  color: "rgba(255, 255, 255, 0.7)",
                  fontFamily: "'Bensoon', sans-serif",
                }}
              >
                {verificationError}
              </p>

              <div 
                className="mb-8 p-4 rounded-lg"
                style={{
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                }}
              >
                <p 
                  className="text-sm"
                  style={{ 
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "'Bensoon', sans-serif",
                  }}
                >
                  {alreadyUsed 
                    ? "This payment link has already been activated for another account. Each purchase can only be used once for security reasons."
                    : "Please contact support if you believe this is an error."}
                </p>
              </div>

              {/* Contact Support Button */}
              <a 
                href="mailto:support@klauyros.com"
                className="inline-block"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                  style={{
                    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                    color: "#FFFFFF",
                    fontFamily: "'Bensoon', sans-serif",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  Contact Support
                  <ArrowRight size={20} />
                </motion.button>
              </a>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
